import React, { useEffect, useMemo, useState } from 'react';
import { Search, Save, RotateCcw, Globe, Filter } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { es as ES, en as EN, flattenStrings } from '../../i18n';
import { refreshTranslationOverrides } from '../../components/Layout';

// Build the full catalog of editable string keys from static i18n.
function buildCatalog() {
  const flatEs = flattenStrings(ES);
  const flatEn = flattenStrings(EN);
  const allKeys = Array.from(new Set([...Object.keys(flatEs), ...Object.keys(flatEn)])).sort();
  return allKeys.map((k) => ({
    key: k,
    defaultEs: flatEs[k] ?? '',
    defaultEn: flatEn[k] ?? '',
    section: k.split('.')[0],
  }));
}

export default function AdminTranslationsPage() {
  const catalog = useMemo(buildCatalog, []);
  const sections = useMemo(
    () => Array.from(new Set(catalog.map((c) => c.section))).sort(),
    [catalog]
  );

  const [overrides, setOverrides] = useState({}); // {key: {es, en}}
  const [drafts, setDrafts] = useState({}); // {key: {es, en}} only modified
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [section, setSection] = useState('all');
  const [query, setQuery] = useState('');
  const [onlyOverridden, setOnlyOverridden] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/translations');
      const map = {};
      for (const row of data || []) {
        map[row.key] = { es: row.es || '', en: row.en || '' };
      }
      setOverrides(map);
      setDrafts({});
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al cargar traducciones');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function currentValue(key, lang) {
    if (drafts[key] && drafts[key][lang] !== undefined) return drafts[key][lang];
    if (overrides[key] && overrides[key][lang] !== undefined) return overrides[key][lang];
    return '';
  }

  function setDraft(key, lang, value) {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [lang]: value },
    }));
  }

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((row) => {
      if (section !== 'all' && row.section !== section) return false;
      if (onlyOverridden && !overrides[row.key] && !drafts[row.key]) return false;
      if (!q) return true;
      return (
        row.key.toLowerCase().includes(q) ||
        (row.defaultEs || '').toLowerCase().includes(q) ||
        (row.defaultEn || '').toLowerCase().includes(q)
      );
    });
  }, [catalog, section, query, onlyOverridden, overrides, drafts]);

  const dirtyKeys = Object.keys(drafts);

  async function saveAll() {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const items = dirtyKeys.map((key) => {
        const row = catalog.find((c) => c.key === key);
        const es = currentValue(key, 'es');
        const en = currentValue(key, 'en');
        // If both equal defaults, send empty to clear override.
        const esOut = es === (row?.defaultEs || '') ? '' : es;
        const enOut = en === (row?.defaultEn || '') ? '' : en;
        return { key, es: esOut, en: enOut };
      });
      await api.put('/admin/translations', { items });
      refreshTranslationOverrides();
      setNotice(`Guardado: ${items.length} clave(s).`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function resetRow(key) {
    setSaving(true);
    setError('');
    try {
      await api.delete(`/admin/translations/${encodeURIComponent(key)}`);
      refreshTranslationOverrides();
      setDrafts((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
      setOverrides((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
      setNotice(`Restablecido: ${key}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al restablecer');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="admin-translations-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Globe size={22} className="text-cmic-600" />
            Traducciones
          </h1>
          <p className="text-charcoal-500 mt-1 max-w-2xl">
            Edita los textos del sitio (ES/EN). Los cambios se guardan como
            sobrescritura de los valores por defecto del código. Deja un campo
            vacío y guarda para restaurar el valor original.
          </p>
        </div>
        <button
          onClick={saveAll}
          disabled={saving || dirtyKeys.length === 0}
          data-testid="admin-translations-save-all"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-cmic-500 text-white hover:bg-cmic-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <Save size={16} />
          {saving ? 'Guardando…' : `Guardar cambios${dirtyKeys.length ? ` (${dirtyKeys.length})` : ''}`}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input
            type="text"
            placeholder="Buscar por clave o contenido…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-translations-search"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-charcoal-400" />
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            data-testid="admin-translations-section"
            className="border border-gray-300 rounded-lg text-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-300"
          >
            <option value="all">Todas las secciones</option>
            {sections.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-charcoal-600 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyOverridden}
            onChange={(e) => setOnlyOverridden(e.target.checked)}
            data-testid="admin-translations-only-overridden"
            className="accent-cmic-500"
          />
          Solo modificadas
        </label>
        <span className="text-xs text-charcoal-500 ml-auto">
          {visibleRows.length} / {catalog.length} claves
        </span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="admin-translations-error">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg p-3" data-testid="admin-translations-notice">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-charcoal-500">
          Cargando…
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-charcoal-500 font-semibold">
            <div className="col-span-3">Clave</div>
            <div className="col-span-4">Español</div>
            <div className="col-span-4">Inglés</div>
            <div className="col-span-1 text-right">Acción</div>
          </div>
          <div className="divide-y divide-gray-100 max-h-[65vh] overflow-y-auto">
            {visibleRows.length === 0 && (
              <div className="px-4 py-10 text-center text-charcoal-500 text-sm">
                Sin resultados.
              </div>
            )}
            {visibleRows.map((row) => {
              const isOverridden = !!overrides[row.key];
              const isDirty = !!drafts[row.key];
              const esVal = currentValue(row.key, 'es') || (isOverridden ? overrides[row.key].es : row.defaultEs);
              const enVal = currentValue(row.key, 'en') || (isOverridden ? overrides[row.key].en : row.defaultEn);
              return (
                <div
                  key={row.key}
                  data-testid={`admin-translations-row-${row.key}`}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 items-start text-sm ${
                    isDirty ? 'bg-amber-50/40' : ''
                  }`}
                >
                  <div className="col-span-3 min-w-0">
                    <div className="font-mono text-xs text-charcoal-700 break-all">{row.key}</div>
                    <div className="mt-1 flex gap-1">
                      {isOverridden && (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-cmic-100 text-cmic-700">
                          modificada
                        </span>
                      )}
                      {isDirty && (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          sin guardar
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-4">
                    <textarea
                      rows={Math.min(4, Math.max(1, Math.ceil((esVal || '').length / 60)))}
                      value={esVal}
                      onChange={(e) => setDraft(row.key, 'es', e.target.value)}
                      placeholder={row.defaultEs}
                      data-testid={`admin-translations-es-${row.key}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300 resize-y"
                    />
                    <div className="text-[10px] text-charcoal-400 mt-1 line-clamp-1" title={row.defaultEs}>
                      Default: {row.defaultEs}
                    </div>
                  </div>
                  <div className="col-span-4">
                    <textarea
                      rows={Math.min(4, Math.max(1, Math.ceil((enVal || '').length / 60)))}
                      value={enVal}
                      onChange={(e) => setDraft(row.key, 'en', e.target.value)}
                      placeholder={row.defaultEn}
                      data-testid={`admin-translations-en-${row.key}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300 resize-y"
                    />
                    <div className="text-[10px] text-charcoal-400 mt-1 line-clamp-1" title={row.defaultEn}>
                      Default: {row.defaultEn}
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {(isOverridden || isDirty) && (
                      <button
                        onClick={() => resetRow(row.key)}
                        title="Restablecer al valor original"
                        data-testid={`admin-translations-reset-${row.key}`}
                        className="p-2 rounded-lg text-charcoal-500 hover:bg-gray-100 hover:text-rose-600 transition-colors"
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
